import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { validateOrg } from '../../Redux/actions/tempEdgeActions';
import types from '../../Redux/actions/types';
import { isExpired } from '../../utils/helpers/jwt';
import { normalizeLoginResult } from '../../utils/helpers/normalizePortalUser';
import { onSessionEvent, SESSION_EVENTS } from '../../utils/helpers/sessionChannel';
import { endSession, startSession } from '../../utils/helpers/sessionManager';
import { getAccessToken, STORAGE_KEYS } from '../../utils/helpers/sessionStore';

const readStoredLogin = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.login);
  if (!raw) return null;

  return normalizeLoginResult(JSON.parse(raw));
};

const shouldRedirectToOrgSelect = (userData) => {
  const path = window.location.pathname;
  const hasAgency = localStorage.getItem(STORAGE_KEYS.agency);
  const isValidated = localStorage.getItem(STORAGE_KEYS.agencyValidated);

  if (!path.includes('/auth') || hasAgency || isValidated) return false;
  if (path.includes('/dashboard') || path.includes('/organization-select')) return false;

  return userData.portalUserList?.length > 0;
};

/**
 * Keeps Redux in sync with the session stored by this tab and by its siblings.
 *
 * Session validity is decided by the access token's own `exp`. The previous
 * version measured it against a `sign_in` timestamp that another tab deleted on
 * every login, so a missing marker read as a 56-year-old session: opening a
 * second tab wiped the shared storage and logged every tab out.
 *
 * The stored login is followed through the `storage` event rather than only
 * through TOKENS_UPDATED. That announcement is published while the tokens are
 * committed, which is before the login payload is written and again before the
 * one carrying the user's roles replaces it, so a sibling tab that only listened
 * for it kept a login with no role in it — and the dashboard, the one screen
 * that needs the role, waited on it forever.
 */
function syncAuth(WrappedComponent) {
  function NewComponent(props) {
    const dispatch = useDispatch();

    useEffect(() => {
      function pullStoredLogin() {
        try {
          const userData = readStoredLogin();
          if (userData) dispatch({ type: types.LOGIN, payload: userData });
        } catch (error) {
          endSession('corrupted_login_payload');
        }
      }

      function handleStorage(evt) {
        if (evt.key === STORAGE_KEYS.login) {
          pullStoredLogin();
          return;
        }

        if (evt.key !== 'choose_agency') return;

        const path = window.location.pathname.split('/');
        if (path[1] !== 'organization-select') return;

        const agency = evt.storageArea?.agency;
        if (agency) validateOrg(JSON.parse(agency));
      }

      function restoreSession() {
        const token = getAccessToken();
        if (!token) return;

        let userData = null;
        try {
          userData = readStoredLogin();
        } catch (error) {
          endSession('corrupted_login_payload');
          return;
        }

        if (!userData) return;

        if (isExpired(token)) {
          endSession('access_token_expired');
          return;
        }

        dispatch({ type: types.LOGIN, payload: userData });
        startSession();

        if (shouldRedirectToOrgSelect(userData)) {
          window.location.href = `/organization-select/${window.location.pathname.split('/')[2] || 'en'}`;
        }
      }

      const unsubscribe = onSessionEvent((message) => {
        if (message.type !== SESSION_EVENTS.TOKENS_UPDATED) return;

        pullStoredLogin();
      });

      restoreSession();
      window.addEventListener('storage', handleStorage);

      return () => {
        unsubscribe();
        window.removeEventListener('storage', handleStorage);
      };
    }, [dispatch]);

    return <WrappedComponent {...props} />;
  }

  return NewComponent;
}

export default syncAuth;
